import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Label,
  FormGroup,
  Table,
  Badge,
  Alert,
  Spinner
} from "reactstrap";
import {
  TbFileDownload,
  TbFileText,
  TbFilter,
  TbSearch,
  TbRefresh,
  TbCalendar,
  TbFileZip,
  TbEye,
  TbChevronDown,
  TbChevronUp,
  TbSortAscending,
  TbSortDescending
} from "react-icons/tb";
import { motion, AnimatePresence } from "framer-motion";
import Select from "react-select";
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { createPortal } from "react-dom";
import { getListarArchivos, getDescargarArchivos, getTipoCasos } from "../../helpers/backend";
import Pagination from "../../components/Common/Pagination_ansync";
import { saveAs } from "file-saver";
import "./ArchivosActas.css";

const ArchivosActas = () => {
  const [archivos, setArchivos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationData, setPaginationData] = useState({
    count: 0,
    next: null,
    previous: null,
    results: []
  });
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
  const [tipoCasoOptions, setTipoCasoOptions] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Opciones para los selects
  const tipoOrdenOptions = [
    { value: "RCD", label: "RCD" },
    { value: "PQRS", label: "PQRS" },
    { value: "PROCESOS COMERCIALES", label: "PROCESOS COMERCIALES" },
    { value: "CORRESPONDENCIA", label: "CORRESPONDENCIA" }
  ];

  // Estados para filtros
  const [filtros, setFiltros] = useState({
    fecha_inicio: "",
    fecha_fin: "",
    tipo_orden: "",
    tipo_caso: "",
    cuenta_contrato: "",
    carga_archivo_id: "",
    numero_planilla: ""
  });

  const limit = 30;

  // Función para obtener la URL base sin /api
  const getBaseUrl = () => {
    const URL = process.env.REACT_APP_API_URL;
    const url = URL.split("/api");
    return url[0];
  };

  const baseUrl = `${getBaseUrl()}/archivos-actas`;

  // Formatear fecha en zona horaria local para evitar desfases por UTC
  const formatDateLocal = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Función para construir URL con filtros
  const buildFilterUrl = (additionalParams = "") => {
    const params = new URLSearchParams();

    // Agregar filtros activos
    Object.entries(filtros).forEach(([key, value]) => {
      if (value && value.trim() !== "") {
        params.set(key, value);
      }
    });

    // Agregar parámetros adicionales (como paginación)
    if (additionalParams) {
      const additionalUrlParams = new URLSearchParams(additionalParams.startsWith('?') ? additionalParams.slice(1) : additionalParams);
      additionalUrlParams.forEach((value, key) => {
        params.set(key, value);
      });
    }

    const queryString = params.toString() ? `?${params.toString()}` : "";
    return queryString;
  };

  // Función para cargar archivos
  const loadArchivos = async (url = "") => {
    setLoading(true);
    try {
      const finalUrl = url || buildFilterUrl(`limit=${limit}&offset=0`);
      const response = await getListarArchivos(finalUrl);

      if (response) {
        // Verificar si la respuesta tiene la estructura esperada
        if (response.results && response.results.archivos_pdf) {
          setPaginationData({
            count: response.count || 0,
            next: response.next || null,
            previous: response.previous || null,
            results: response.results.archivos_pdf || []
          });
          setArchivos(response.results.archivos_pdf || []);
        } else if (response.data && response.data.results && response.data.results.archivos_pdf) {
          // Estructura alternativa con data wrapper
          setPaginationData({
            count: response.data.count || 0,
            next: response.data.next || null,
            previous: response.data.previous || null,
            results: response.data.results.archivos_pdf || []
          });
          setArchivos(response.data.results.archivos_pdf || []);
        } else {
          console.log('Estructura de respuesta no reconocida:', response);
        }
      }
    } catch (error) {
      console.error("Error al cargar archivos:", error);
      // En caso de error, mostrar estructura vacía
      setPaginationData({
        count: 0,
        next: null,
        previous: null,
        results: []
      });
      setArchivos([]);
    } finally {
      setLoading(false);
    }
  };

  // Función para descargar archivo individual
  const downloadFile = async (archivo) => {
    try {
      if (archivo.archivo_pdf_url) {
        const baseUrlForDownload = getBaseUrl();
        const fullUrlBase = archivo.archivo_pdf_url.startsWith('http')
          ? archivo.archivo_pdf_url
          : `${baseUrlForDownload}${archivo.archivo_pdf_url}`;
        // Rompe-caché: el PDF se regenera con el mismo nombre; forzar la versión nueva
        const fullUrl = `${fullUrlBase}${fullUrlBase.includes('?') ? '&' : '?'}t=${Date.now()}`;

        window.open(fullUrl, '_blank');
      } else {
        alert("URL de archivo no disponible");
      }
    } catch (error) {
      console.error("Error al descargar archivo:", error);
      alert("Error al descargar el archivo");
    }
  };

  // Función para descargar archivos con filtros en ZIP
  const downloadSelectedAsZip = async () => {
    setDownloadingZip(true);

    try {
      // Construir URL con filtros activos
      const filterUrl = buildFilterUrl();

      // Llamar al endpoint que devuelve el ZIP directamente
      const response = await getDescargarArchivos(filterUrl);

      // Verificar que la respuesta sea exitosa y contenga datos
      if (response && response.data) {
        // Verificar que el blob no esté vacío y sea del tipo correcto
        if (response.data.size > 0) {
          const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
          saveAs(response.data, `archivos_actas_${timestamp}.zip`);
        } else {
          throw new Error('El archivo ZIP está vacío o no se pudo generar');
        }
      } else {
        throw new Error('No se recibió el archivo ZIP del servidor');
      }

    } catch (error) {
      console.error("Error al descargar ZIP:", error);

      // Mostrar mensaje de error más específico
      let errorMessage = "Error al descargar el archivo ZIP";

      if (error.status === 406) {
        errorMessage = "Error 406: El servidor no puede generar el archivo en el formato solicitado. Verifique los filtros aplicados.";
      } else if (error.status === 404) {
        errorMessage = "No se encontraron archivos que coincidan con los filtros aplicados.";
      } else if (error.status === 500) {
        errorMessage = "Error interno del servidor. Intente nuevamente más tarde.";
      } else if (error.isNetworkError) {
        errorMessage = "Error de conexión. Verifique su conexión a internet.";
      }

      alert(errorMessage);
    } finally {
      setDownloadingZip(false);
    }
  };

  // Función para manejar cambios de página
  const handlePageChange = (url) => {
    loadArchivos(url);
  };

  // Funciones para manejar cambios en selects
  const onTipoOrdenChange = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setFiltros(prev => ({
      ...prev,
      tipo_orden: value,
      numero_planilla: value === "CORRESPONDENCIA" ? prev.numero_planilla : ""
    }));
  };

  const onTipoReclamoChange = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setFiltros(prev => ({
      ...prev,
      tipo_caso: value
    }));
  };

  const onDateRangeChange = (value) => {
    setDateRange(value);
    if (value && value.length === 2 && value[0] && value[1]) {
      const startDate = formatDateLocal(value[0]);
      const endDate = formatDateLocal(value[1]);
      console.log('Fechas formateadas (YYYY-MM-DD, local):', { startDate, endDate });
      setFiltros(prev => {
        const next = {
          ...prev,
          fecha_inicio: startDate,
          fecha_fin: endDate
        };
        return next;
      });
    } else {
      setFiltros(prev => {
        const next = {
          ...prev,
          fecha_inicio: "",
          fecha_fin: ""
        };
        return next;
      });
    }
  };

  // Función para manejar cambios en filtros
  const handleFilterChange = (field, value) => {
    setFiltros(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Función para aplicar filtros
  const applyFilters = () => {
    const filterUrl = buildFilterUrl(`limit=${limit}&offset=0`);
    console.log('Aplicar filtros -> URL completa:', `${baseUrl}${filterUrl}`);
    loadArchivos(filterUrl);
  };

  // Función para limpiar filtros
  const clearFilters = () => {
    setFiltros({
      fecha_inicio: "",
      fecha_fin: "",
      tipo_orden: "",
      tipo_caso: "",
      cuenta_contrato: "",
      carga_archivo_id: "",
      numero_planilla: ""
    });
    setDateRange([null, null]);
    setShowCalendar(false);
    console.log('Limpiar filtros -> URL completa:', `${baseUrl}?limit=${limit}&offset=0`);
    loadArchivos(`?limit=${limit}&offset=0`);
  };

  // Función para obtener datos de tipos de caso (Fuente)
  const getTipoCasosData = async () => {
    try {
      const response = await getTipoCasos();
      const tiposCaso = response.data.tipos_caso || [];
      const options = tiposCaso.map(tipo => ({
        value: tipo,
        label: tipo
      }));
      setTipoCasoOptions(options);
    } catch (error) {
      console.error("Error al cargar tipos de caso:", error);
      setTipoCasoOptions([]);
    }
  };

  // Función para ordenar la tabla
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Archivos ordenados
  const sortedArchivos = React.useMemo(() => {
    if (!sortConfig.key) return archivos;

    return [...archivos].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [archivos, sortConfig]);

  // Cargar datos al montar el componente
  useEffect(() => {
    loadArchivos();
    getTipoCasosData();
  }, []);

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    hover: { y: -5, transition: { duration: 0.2 } }
  };

  const rowVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 },
    hover: { backgroundColor: "#f8f9ff" }
  };

  return (
    <div className="page-content ">
      <Container fluid>
        <Row>
          <Col xs="12">
            {/* Header Mejorado */}
            <motion.div
              className="page-header mb-4"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="d-flex align-items-center justify-content-between">

                <Row className="align-items-center">
                  <Col>
                    <div className="d-flex align-items-center">
                      <div className="gallery-icon-wrapper me-3">
                        <TbFileText size={28} className="text-white" />
                      </div>
                      <div>
                        <h1 className="gallery-title mb-1">Archivos y Actas PDF</h1>
                        <p className="gallery-subtitle mb-0">
                          Descarga las Actas de Visita Digital en PDF para su consulta y seguimiento.
                        </p>
                      </div>
                    </div>
                  </Col>
                  <Col xs="auto">

                  </Col>
                </Row>
                <Badge color="light" className="text-dark fs-6">
                  {paginationData.count} archivos en total
                </Badge>
              </div>
            </motion.div>

            {/* Filtros Mejorados */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card className="filters-card border-0 shadow-sm mb-4">
                <CardBody className="p-0">
                  <div className="filters-header p-3 border-bottom">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <TbFilter size={20} className="text-primary me-2" />
                        <h5 className="filters-title mb-0">Filtros de Búsqueda</h5>
                      </div>
                      <Button
                        color="link"
                        className="p-0"
                        onClick={() => setShowFilters(!showFilters)}
                      >
                        {showFilters ? <TbChevronUp size={20} /> : <TbChevronDown size={20} />}
                      </Button>
                    </div>
                  </div>

                  <AnimatePresence>
                    {showFilters && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="filters-body p-3">
                          <Row className="align-items-end g-3">
                            <Col md={3}>
                              <FormGroup className="mb-0">
                                <Label className="filter-label">Rango de Fechas</Label>
                                <div className="position-relative">
                                  <Button
                                    color="outline-primary"
                                    onClick={(e) => {
                                      const rect = e.target.getBoundingClientRect();
                                      setCalendarPosition({
                                        top: rect.bottom + window.scrollY,
                                        left: rect.left + window.scrollX
                                      });
                                      setShowCalendar(!showCalendar);
                                    }}
                                    className="w-100 text-start date-range-btn"
                                  >
                                    <TbCalendar className="me-2" />
                                    {dateRange[0] && dateRange[1]
                                      ? `${dateRange[0].toLocaleDateString()} - ${dateRange[1].toLocaleDateString()}`
                                      : "Seleccionar fechas"
                                    }
                                  </Button>
                                  {showCalendar && createPortal(
                                    <div
                                      className="calendar-popover"
                                      style={{
                                        top: calendarPosition.top,
                                        left: calendarPosition.left,
                                      }}
                                    >
                                      <Calendar
                                        onChange={onDateRangeChange}
                                        value={dateRange}
                                        selectRange={true}
                                        className="react-calendar-custom"
                                      />
                                      <div className="calendar-actions p-2 border-top">
                                        <Button
                                          color="primary"
                                          size="sm"
                                          onClick={() => setShowCalendar(false)}
                                          className="me-2"
                                        >
                                          Aplicar
                                        </Button>
                                        <Button
                                          color="secondary"
                                          size="sm"
                                          onClick={() => {
                                            setDateRange([null, null]);
                                            onDateRangeChange([null, null]);
                                            setShowCalendar(false);
                                          }}
                                        >
                                          Limpiar
                                        </Button>
                                      </div>
                                    </div>,
                                    document.body
                                  )}
                                </div>
                              </FormGroup>
                            </Col>

                            <Col md={2}>
                              <FormGroup className="mb-0">
                                <Label className="filter-label">Tipo Orden</Label>
                                <Select
                                  value={tipoOrdenOptions.find(option => option.value === filtros.tipo_orden) || null}
                                  onChange={onTipoOrdenChange}
                                  options={tipoOrdenOptions}
                                  placeholder="Tipo orden"
                                  isClearable
                                  menuPortalTarget={document.body}
                                  className="react-select-container"
                                  classNamePrefix="react-select"
                                />
                              </FormGroup>
                            </Col>

                            <Col md={2}>
                              <FormGroup className="mb-0">
                                <Label className="filter-label">Fuente</Label>
                                <Select
                                  value={tipoCasoOptions.find(option => option.value === filtros.tipo_caso) || null}
                                  onChange={onTipoReclamoChange}
                                  options={tipoCasoOptions}
                                  placeholder="Fuente"
                                  isClearable
                                  menuPortalTarget={document.body}
                                  className="react-select-container"
                                  classNamePrefix="react-select"
                                />
                              </FormGroup>
                            </Col>

                            <Col md={3}>
                              <FormGroup className="mb-0">
                                <Label className="filter-label">Cuenta/Contrato</Label>
                                <div className="search-input-wrapper">
                                  <TbSearch className="search-icon" />
                                  <Input
                                    type="text"
                                    placeholder="Buscar cuenta/contrato"
                                    value={filtros.cuenta_contrato}
                                    onChange={(e) => handleFilterChange("cuenta_contrato", e.target.value)}
                                    className="search-input"
                                  />
                                </div>
                              </FormGroup>
                            </Col>

                            <Col md={2}>
                              <div className="filter-actions d-grid gap-2">
                                <Button
                                  color="primary"
                                  onClick={applyFilters}
                                  className="filter-apply-btn"
                                >
                                  <TbSearch className="me-2" />
                                  Buscar
                                </Button>
                                <Button
                                  color="outline-secondary"
                                  onClick={clearFilters}
                                  className="filter-clear-btn"
                                >
                                  <TbRefresh className="me-2" />
                                  Limpiar
                                </Button>
                              </div>
                            </Col>
                          </Row>

                          {filtros.tipo_orden === "CORRESPONDENCIA" && (
                            <Row className="align-items-end g-3 mt-2">
                              <Col md={4}>
                                <FormGroup className="mb-0">
                                  <Label className="filter-label">Número de Planilla</Label>
                                  <div className="search-input-wrapper">
                                    <TbSearch className="search-icon" />
                                    <Input
                                      type="text"
                                      placeholder="Buscar por planilla"
                                      value={filtros.numero_planilla}
                                      onChange={(e) => handleFilterChange("numero_planilla", e.target.value)}
                                      className="search-input"
                                    />
                                  </div>
                                </FormGroup>
                              </Col>
                            </Row>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </CardBody>
              </Card>
            </motion.div>

            {/* Acciones de Descarga */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card className="actions-card border-0 shadow-sm mb-4">
                <CardBody>
                  <Row className="align-items-center">
                    <Col md={6}>
                      <div className="d-flex align-items-center">
                        <div className="actions-info">
                          <h6 className="actions-title mb-1">
                            Descarga Masiva de Archivos
                          </h6>
                          <p className="actions-subtitle mb-0 text-muted">
                            El archivo ZIP incluirá todos los documentos que coincidan con los filtros aplicados
                          </p>
                        </div>
                      </div>
                    </Col>
                    <Col md={6} className="text-end">
                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Button
                          color="success"
                          onClick={downloadSelectedAsZip}
                          disabled={downloadingZip || archivos.length === 0}
                          className="download-zip-btn"
                          size="lg"
                        >
                          {downloadingZip ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Generando ZIP...
                            </>
                          ) : (
                            <>
                              <TbFileZip className="me-2" size={20} />
                              Descargar ZIP
                            </>
                          )}
                        </Button>
                      </motion.div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </motion.div>

            {/* Tabla de Archivos */}
            <AnimatePresence>
              {loading ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="loading-card border-0 shadow-sm">
                    <CardBody className="text-center py-5">
                      <div className="loading-spinner-wrapper mb-3">
                        <Spinner color="primary" className="loading-spinner" />
                      </div>
                      <h5 className="loading-title mb-2">Cargando archivos...</h5>
                      <p className="loading-subtitle text-muted mb-0">
                        Estamos buscando los archivos que coinciden con tus criterios
                      </p>
                    </CardBody>
                  </Card>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  {archivos.length > 0 ? (
                    <Card className="files-table-card border-0 shadow-sm">
                      <CardBody className="p-0">
                        <div className="table-responsive">
                          <Table className="files-table table-hover mb-0">
                            <thead className="files-table-header">
                              <tr>
                                <th
                                  className="sortable-header"
                                  onClick={() => handleSort('ot_nombre')}
                                >
                                  <div className="d-flex align-items-center">
                                    OT
                                    {sortConfig.key === 'ot_nombre' && (
                                      sortConfig.direction === 'asc' ?
                                        <TbSortAscending className="ms-1" /> :
                                        <TbSortDescending className="ms-1" />
                                    )}
                                  </div>
                                </th>
                                <th>Cliente</th>
                                <th>Cuenta/Contrato</th>
                                <th>Archivo PDF</th>
                                <th
                                  className="sortable-header"
                                  onClick={() => handleSort('fecha_informe')}
                                >
                                  <div className="d-flex align-items-center">
                                    Fecha
                                    {sortConfig.key === 'fecha_informe' && (
                                      sortConfig.direction === 'asc' ?
                                        <TbSortAscending className="ms-1" /> :
                                        <TbSortDescending className="ms-1" />
                                    )}
                                  </div>
                                </th>
                                <th>Estado</th>
                                <th>Fuente</th>
                                <th className="text-center">Acciones</th>
                              </tr>
                            </thead>
                            <tbody>
                              <AnimatePresence>
                                {sortedArchivos.map((archivo, index) => (
                                  <motion.tr
                                    key={archivo.caso_id || index}
                                    variants={rowVariants}
                                    initial="hidden"
                                    animate="visible"
                                    whileHover="hover"
                                    transition={{ duration: 0.2, delay: index * 0.05 }}
                                    className="files-table-row"
                                  >
                                    <td>
                                      <Badge color="primary" className="ot-badge">
                                        {archivo.ot_nombre || 'N/A'}
                                      </Badge>
                                    </td>
                                    <td>
                                      <div className="client-info">
                                        <div className="client-name">
                                          {archivo.cliente_nombre || 'N/A'}
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <code className="account-code">
                                        {archivo.cuenta_contrato || 'N/A'}
                                      </code>
                                    </td>
                                    <td>
                                      <div className="file-info">
                                        <div className="file-icon-wrapper me-2">
                                          <TbFileText className="file-icon" />
                                        </div>
                                        <div className="file-details">
                                          <div className="file-name">
                                            {archivo.archivo_pdf_nombre || 'N/A'}
                                          </div>
                                          <small className="file-size text-muted">
                                            PDF Document
                                          </small>
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="date-info">
                                        <div className="date-text">
                                          {archivo.fecha_informe || 'N/A'}
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <Badge
                                        color={archivo.estado === 'FINALIZADO' ? 'success' : 'warning'}
                                        className="p-2 border-radius-30"
                                      >
                                        <b>{archivo.estado || 'N/A'}</b>
                                      </Badge>
                                    </td>
                                    <td>
                                      <div className="source-info">
                                        {archivo.tipo_cso || 'N/A'}
                                      </div>
                                    </td>
                                    <td className="text-center">
                                      <motion.div
                                        className="action-buttons"
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                      >
                                        <Button
                                          color="primary"
                                          size="sm"
                                          onClick={() => downloadFile(archivo)}
                                          className="action-btn "
                                          title="Descargar archivo"
                                        >
                                          <TbFileDownload size={16} />
                                        </Button>
                                      </motion.div>
                                    </td>
                                  </motion.tr>
                                ))}
                              </AnimatePresence>
                            </tbody>
                          </Table>
                        </div>
                      </CardBody>
                    </Card>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Card className="no-data-card border-0 shadow-sm">
                        <CardBody className="text-center py-5">
                          <div className="no-data-icon mb-3">
                            <TbFileText size={64} className="text-muted" />
                          </div>
                          <h5 className="no-data-title mb-2">No se encontraron archivos</h5>
                          <p className="no-data-subtitle text-muted mb-3">
                            No hay archivos que coincidan con los filtros aplicados.
                          </p>
                          <Button
                            color="primary"
                            onClick={clearFilters}
                            className="no-data-action-btn"
                          >
                            <TbRefresh className="me-2" />
                            Limpiar filtros
                          </Button>
                        </CardBody>
                      </Card>
                    </motion.div>
                  )}

                  {/* Paginación */}
                  {paginationData.count > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.4 }}
                      className="mt-4"
                    >
                      <Pagination
                        count={paginationData.count}
                        next={paginationData.next}
                        previous={paginationData.previous}
                        limit={limit}
                        baseUrl={baseUrl}
                        filterFunction={handlePageChange}
                      />
                    </motion.div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ArchivosActas;